import { RefObject, useLayoutEffect, useRef } from "react";

interface UseSlidingTabIndicatorOptions {
  tabListRef: RefObject<HTMLElement>;
  selectedTabRef: RefObject<HTMLElement>;
  /** Re-measure when selection or layout context changes. */
  selectedId: string | number | undefined;
  /** Extra deps that affect tab positions (e.g. carousel window). */
  layoutKey?: string | number;
}

function readPaddingInset(element: HTMLElement) {
  const style = getComputedStyle(element);
  return {
    top: parseFloat(style.paddingTop) || 0,
    bottom: parseFloat(style.paddingBottom) || 0,
  };
}

/**
 * Positions a sliding pill behind the active tab.
 * Vertical size uses the tablist padding inset so the pill always matches the
 * track border; horizontal position/size are measured from the active tab.
 */
export function useSlidingTabIndicator({
  tabListRef,
  selectedTabRef,
  selectedId,
  layoutKey,
}: UseSlidingTabIndicatorOptions): RefObject<HTMLSpanElement> {
  const pillRef = useRef<HTMLSpanElement>(null);
  const hasMountedRef = useRef(false);
  const prevSelectedIdRef = useRef(selectedId);
  const prevLayoutKeyRef = useRef(layoutKey);
  const suppressResizeUntilRef = useRef(0);

  useLayoutEffect(() => {
    const tabList = tabListRef.current;
    const selectedTab = selectedTabRef.current;
    const pill = pillRef.current;

    if (!tabList || !selectedTab || !pill) {
      return;
    }

    const selectedChanged = prevSelectedIdRef.current !== selectedId;
    const layoutChanged = prevLayoutKeyRef.current !== layoutKey;
    prevSelectedIdRef.current = selectedId;
    prevLayoutKeyRef.current = layoutKey;

    const shouldAnimate =
      hasMountedRef.current && selectedChanged && !layoutChanged;

    const applyPill = (snap: boolean) => {
      const tab = selectedTabRef.current;
      const pillEl = pillRef.current;
      const list = tabListRef.current;
      if (!tab || !pillEl || !list) {
        return;
      }

      if (snap) {
        pillEl.style.transition = "none";
      }

      const inset = readPaddingInset(list);
      const listRect = list.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();

      pillEl.style.top = `${inset.top}px`;
      pillEl.style.bottom = `${inset.bottom}px`;
      pillEl.style.height = "auto";
      pillEl.style.width = `${tabRect.width}px`;
      pillEl.style.transform = `translateX(${tabRect.left - listRect.left}px)`;

      if (snap) {
        void list.offsetHeight;
        pillEl.style.removeProperty("transition");
      }
    };

    applyPill(!shouldAnimate);

    if (selectedTab.offsetWidth === 0) {
      requestAnimationFrame(() => applyPill(!shouldAnimate));
    }

    if (shouldAnimate) {
      const durationMs = getComputedStyle(pill).getPropertyValue(
        "--task-type-tabs-dur",
      );
      const parsedMs = Number.parseFloat(durationMs);
      suppressResizeUntilRef.current =
        performance.now() + (Number.isFinite(parsedMs) ? parsedMs : 250) + 50;
    }

    hasMountedRef.current = true;

    const observer = new ResizeObserver(() => {
      if (performance.now() < suppressResizeUntilRef.current) {
        return;
      }
      applyPill(true);
    });
    observer.observe(tabList);

    return () => {
      observer.disconnect();
    };
  }, [tabListRef, selectedTabRef, selectedId, layoutKey]);

  return pillRef;
}
